import type React from 'react';
import { useEffect, useRef } from 'react';
import { noop } from 'lodash';

import mixpanel from '~/components/CmsMain/mixpanel';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import type { MinimizedComponentBaseProps, MinimizedType } from '~/components/core/MinimizedBar/types';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';

const EMAIL_TYPE: MinimizedType = 'EMAIL';

// The editors handed over here are mostly untyped `.jsx`, and their callbacks take whatever their own
// caller passes on -- `onSendEmail` receives the created communication, for instance.
type EmailDialogCallback = (...args: never[]) => unknown;

export interface MinimizedEmailDialogProps extends MinimizedComponentBaseProps {
  onClose?: EmailDialogCallback;
  onSendEmail?: EmailDialogCallback;
}

/** The props the registry owns, so a caller neither has to supply them nor can override them. */
type RegistryOwnedProps = keyof MinimizedComponentBaseProps | 'onClose' | 'onSendEmail';

export interface MinimizedEmailDialogLauncherProps<T extends MinimizedEmailDialogProps> {
  /** What the minimized bar reads while the editor is collapsed. */
  barHeader: string;
  /** A `MIXPANEL_EVENT_SOURCES` value naming the screen the editor was opened from. */
  mixpanelSource: string;
  dialogComponent: React.FC<T>;
  dialogProps: Omit<T, RegistryOwnedProps> & {
    /** Awaited before the editor is retired, for whatever the caller used to do on close. */
    onClose?: EmailDialogCallback;
    /** Ditto for a successful send: the registry entry is retired after the handler resolves. */
    onSendEmail?: EmailDialogCallback;
  };
  /**
   * Called once the editor belongs to the registry, so the caller can drop whatever state was
   * rendering this launcher. The editor outlives that state on purpose.
   */
  onLaunched: () => void;
}

/**
 * Hands an email editor to the minimized-dialog registry instead of rendering it in place, which is
 * what gives it a minimize button and lets a half-written email survive leaving the screen that
 * opened it. Renders nothing itself: mount it in the slot the editor used to occupy.
 *
 * The whole authenticated app is wrapped in a registry (`withMinimizedDialog` in `CmsMain`), and
 * `ClaimContextProvider` mounts a nested one, so a launcher has a registry to hand the editor to on
 * every screen. The editor an app-level registry renders is a sibling of the screen rather than a
 * child of it, so it must be self-contained -- fetch its own claim and provide its own claim context
 * -- rather than reading a context the screen happened to supply.
 */
function MinimizedEmailDialogLauncher<T extends MinimizedEmailDialogProps>({
  barHeader,
  mixpanelSource,
  dialogComponent,
  dialogProps,
  onLaunched,
}: MinimizedEmailDialogLauncherProps<T>): null {
  const { add } = useMinimizedDialogs();
  const hasRegisteredRef = useRef(false);

  useEffect(() => {
    if (hasRegisteredRef.current) {
      return;
    }
    // `dialogProps` is rebuilt on every render, so the ref -- not the dep array -- is what keeps this
    // to one editor.
    hasRegisteredRef.current = true;

    const { onClose, onSendEmail, ...restDialogProps } = dialogProps;

    let closeDialog = noop;
    // Retiring the registry entry is now the only thing that unmounts the editor, so every path that
    // used to close it by flipping the caller's own state has to go through here -- both halves of
    // the pair, not just the close button. The caller's own work runs first, the way it did when the
    // caller owned the open/closed state: a handler that throws then leaves the editor on screen with
    // the draft intact rather than tearing it down and leaving only a toast.
    const runThenClose =
      (handler?: EmailDialogCallback) =>
      async (...args: never[]) => {
        await handler?.(...args);
        closeDialog();
      };

    const { handleCloseDialog } = add({
      barHeader,
      type: EMAIL_TYPE,
      dialogComponent,
      dialogProps: {
        ...restDialogProps,
        onClose: runThenClose(onClose),
        // Injected only when the caller has a send handler: an editor that closes itself through
        // `onClose` after sending (the suggested-email dialog does) takes no `onSendEmail` at all.
        ...(onSendEmail ? { onSendEmail: runThenClose(onSendEmail) } : {}),
        onMinimized: () => {
          mixpanel.track(MIXPANEL_EVENTS.MINIMIZED_DIALOG, { source: mixpanelSource, type: EMAIL_TYPE });
        },
      },
    });
    closeDialog = handleCloseDialog;

    // Deliberately no cleanup that closes the editor: the registry renders it outside this subtree
    // precisely so that minimizing survives navigating away from the screen that opened it.
    onLaunched();
  }, [add, barHeader, dialogComponent, dialogProps, mixpanelSource, onLaunched]);

  return null;
}

export default MinimizedEmailDialogLauncher;
